import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { applyConnectorSetupGuideEvent, type ConnectorSetupGuideEvent } from "../../../api/connectorSetupApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { reportUiError, toUiError } from "../../../errors/uiError";
import { useSession } from "../../../session/sessionContext";
import { HOME_PATH } from "../../../shared/routing/appPaths";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { AgentsStep, DoneStep, PairStep, ServerAddressStep, StartStep, WelcomeStep } from "./SetupStepBodies";
import SetupStepList, { type SetupView } from "./SetupStepList";
import { currentStepOf } from "./setupProgress";
import { guideStartOf, useConnectorSetup, type ConnectorSetupView } from "./useConnectorSetup";

/**
 * `/get-started`: the guided connector setup (onboarding.md C-A1, Shape A; F32). A page, not a
 * dialog: it waits beside the person's PC and moves on by itself as the PC reports in.
 * Opening it the first time introduces the guide; "Not now" puts it away. "Set up my PC" and
 * "Connect your PC" start it at once, at the current step (F46).
 */
export default function ConnectorSetupPage() {
  const { t } = useTranslation();
  const { state, reload, refresh } = useConnectorSetup();
  return (
    <div className="w-full max-w-4xl space-y-4">
      <h1 className="app-title text-xl font-semibold">{t("connectors.setup.title")}</h1>
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />
      {state.status === "loaded" ? <SetupGuide view={state.value} onChanged={refresh} /> : null}
    </div>
  );
}

function SetupGuide({ view, onChanged }: { view: ConnectorSetupView; onChanged: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useSession();
  const isAdministrator = session.status === "signedIn" && session.session.account.accountRole === "administrator";
  const submission = useSubmission();
  const startedAt = guideStartOf(useLocation().state);
  const startedAtOnce = startedAt !== null;
  // Whoever has not started it, or put it away, meets the welcome first, unless a button started it.
  const [chosen, setChosen] = useState<SetupView | null>(() => (startedAt === "pair" ? "pair" : view.guide.state === "active" || startedAtOnce ? null : "welcome"));
  // The end screen follows the facts: a PC revoked meanwhile takes it away again.
  const followed = chosen === "done" && !view.progress.complete ? null : chosen;
  const shown: SetupView = followed ?? currentStepOf(view.progress) ?? "done";

  // The first visit introduces the guide, once (OB-2): it is active from now on.
  const introduced = useRef(false);
  useEffect(() => {
    if (view.guide.state !== "not_introduced" || introduced.current) return;
    introduced.current = true;
    applyConnectorSetupGuideEvent("introduce").then(onChanged, (caught: unknown) => {
      reportUiError(toUiError(caught));
    });
  }, [view.guide.state, onChanged]);

  // Started at once while put away: the guide resumes, as "Start" on the welcome would.
  const resumed = useRef(false);
  useEffect(() => {
    if (!startedAtOnce || view.guide.state !== "dismissed" || resumed.current) return;
    resumed.current = true;
    applyConnectorSetupGuideEvent("resume").then(onChanged, (caught: unknown) => {
      reportUiError(toUiError(caught));
    });
  }, [startedAtOnce, view.guide.state, onChanged]);

  const apply = (event: ConnectorSetupGuideEvent, then: () => void) => {
    submission.submit(async () => {
      await applyConnectorSetupGuideEvent(event);
      then();
    });
  };
  const putAway = () => {
    if (view.guide.state === "dismissed") void navigate(HOME_PATH);
    else apply("dismiss", () => void navigate(HOME_PATH));
  };
  // Going on with a put-away guide, from the welcome or by picking a step, resumes it.
  const choose = (next: SetupView | null) => {
    if (view.guide.state === "dismissed" && next !== "welcome") apply("resume", () => { setChosen(next); onChanged(); });
    else setChosen(next);
  };
  const start = () => { choose(null); };
  const busy = submission.state.status === "submitting";

  return (
    <div className="space-y-3">
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} /> : null}
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2.5fr)]">
        <SetupStepList progress={view.progress} shown={shown} onChoose={choose} />
        <section className="app-card space-y-4 p-4" aria-live="polite">
          <h2 className="app-title text-base font-semibold">{t(`connectors.setup.steps.${shown}`)}</h2>
          {shown === "welcome" ? <WelcomeStep busy={busy} onStart={start} onNotNow={putAway} /> : null}
          {shown === "server_address" ? <ServerAddressStep isAdministrator={isAdministrator} /> : null}
          {shown === "pair" ? <PairStep waiting={view.progress.phase === "needs_pairing"} /> : null}
          {shown === "start" ? <StartStep connectors={view.connectors} /> : null}
          {shown === "agents" ? <AgentsStep connectors={view.connectors} onChecked={onChanged} /> : null}
          {shown === "done" ? <DoneStep /> : null}
        </section>
      </div>
      {shown === "welcome" ? null : (
        <div className="flex justify-end gap-2">
          {chosen === null ? null : <AppButton variant="ghost" size="sm" onClick={() => { choose(null); }}>{t("connectors.setup.followCurrent")}</AppButton>}
          {view.progress.complete ? null : <AppButton variant="secondary" size="sm" disabled={busy} onClick={putAway}>{t("connectors.setup.later")}</AppButton>}
        </div>
      )}
    </div>
  );
}
